import React, { useEffect, useState } from "react";
import axios from "axios";
import Cookies from "js-cookie";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import * as XLSX from "xlsx";
import config from "../config";

// PrimeReact imports
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { ProgressSpinner } from "primereact/progressspinner";
import { Tag } from "primereact/tag";
import { Calendar } from "primereact/calendar";
import { InputText } from "primereact/inputtext";
import { Card } from "primereact/card";
import { Divider } from "primereact/divider";
import { Skeleton } from "primereact/skeleton";

import "primereact/resources/themes/lara-light-blue/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import "./StockAssessmentPage.css";

const StockAssessmentPage = ({ darkTheme = false }) => {
  const [loading, setLoading] = useState(true);
  const [stockArray, setStockArray] = useState([]);
  const [filteredStockArray, setFilteredStockArray] = useState([]);
  const [orderCount, setOrderCount] = useState(0);
  const [packedCount, setPackedCount] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [isFetching, setIsFetching] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const navigate = useNavigate();

  const fetchStockData = async (date) => {
    setIsFetching(true);
    try {
      const token = Cookies.get("token");
      if (!token) {
        navigate("/");
        return;
      }

      const response = await axios.get(`${config.apiBaseUrl}/orders/all-orders`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const allOrders = response.data.orders || [];
      const selectedDay = moment(date).startOf("day");

      const filteredOrders = allOrders.filter((order) => {
        const orderDate = moment(order.createdAt);
        return orderDate.isSame(selectedDay, "day");
      });

      const completedOrders = filteredOrders.filter(o => o.packed_status === "Completed");
      const pendingOrders = filteredOrders.filter(o => o.packed_status !== "Completed");

      const aggregateProducts = (orders) => {
        const result = {};
        orders.forEach(order => {
          order.products.forEach(product => {
            const baseName = product.name || "Unknown Product";
            const sku = product.sku || "N/A";
            const variantMatch = sku.match(/(\d+\s*g)|(\d+g)/i);
            const variant = variantMatch ? variantMatch[0].replace(/\s+/g, "") : "Unknown";
            const nameWithVariant = `${baseName} ${variant}`;
            const quantity = Number(product.quantity) || 0;

            if (result[nameWithVariant]) {
              result[nameWithVariant].quantity += quantity;
            } else {
              result[nameWithVariant] = {
                sku,
                quantity,
              };
            }
          });
        });
        return result;
      };

      const totalStock = aggregateProducts(filteredOrders);
      const packedStock = aggregateProducts(completedOrders);
      const remainingStock = aggregateProducts(pendingOrders);

      const mergedArray = Object.keys(totalStock).map((name, index) => ({
        key: index + 1,
        sno: index + 1,
        productName: name,
        sku: totalStock[name].sku,
        totalOrdered: totalStock[name].quantity,
        packed: packedStock[name]?.quantity || 0,
        remaining: remainingStock[name]?.quantity || 0,
      }));

      mergedArray.sort((a, b) => b.totalOrdered - a.totalOrdered);

      setStockArray(mergedArray);
      setFilteredStockArray(mergedArray);
      setOrderCount(filteredOrders.length);
      setPackedCount(completedOrders.length);
      setPendingCount(pendingOrders.length);
    } catch (err) {
      console.error("[StockAssessment] Error fetching data:", err);
    } finally {
      setLoading(false);
      setIsFetching(false);
    }
  };

  useEffect(() => {
    fetchStockData(selectedDate);
  }, [selectedDate, navigate]);

  // Search functionality
  useEffect(() => {
    if (searchTerm.trim() === "") {
      setFilteredStockArray(stockArray);
    } else {
      const filtered = stockArray.filter(item =>
        item.productName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.sku.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredStockArray(filtered);
    }
  }, [searchTerm, stockArray]);

  const exportToExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(filteredStockArray);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Stock Assessment");
    XLSX.writeFile(workbook, `StockAssessment_${moment(selectedDate).format("YYYY-MM-DD")}.xlsx`);
  };

  const statusBodyTemplate = (rowData) => {
    return (
      <Tag
        value={rowData.remaining === 0 ? "Completed" : "Pending"}
        severity={rowData.remaining === 0 ? "success" : "warning"}
        className="status-tag"
      />
    );
  };

  const quantityBodyTemplate = (rowData, field) => {
    const value = rowData[field];
    let className = "quantity ";
    
    if (field === "remaining" && value > 0) {
      className += "remaining-high";
    } else if (field === "packed") {
      className += "packed-high";
    }
    
    return (
      <span className={className}>
        {value}
      </span>
    );
  };

  const productNameBodyTemplate = (rowData) => {
    return (
      <div className="product-cell">
        <span className={`product-name ${darkTheme ? 'text-white' : 'text-gray-900'}`}>
          {rowData.productName}
        </span>
        <span className="product-sku">{rowData.sku}</span>
      </div>
    );
  };

  const rowNumberBodyTemplate = (_, { rowIndex }) => {
    return (
      <div className="row-number">
        {rowIndex + 1}
      </div>
    );
  };

  const summaryCards = [
    {
      title: "Total Orders",
      value: orderCount,
      icon: "pi-shopping-cart",
      gradient: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      iconBg: "#667eea"
    },
    {
      title: "Packed Orders",
      value: packedCount,
      icon: "pi-check-circle",
      gradient: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
      iconBg: "#4facfe"
    },
    {
      title: "Pending Orders",
      value: pendingCount,
      icon: "pi-clock",
      gradient: "linear-gradient(135deg, #fa709a 0%, #fee140 100%)",
      iconBg: "#fa709a"
    },
    {
      title: "Products",
      value: stockArray.length,
      icon: "pi-box",
      gradient: "linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)",
      iconBg: "#a8edea"
    }
  ];

  const renderSkeleton = () => (
    <div className="skeleton-container">
      <Card className="skeleton-card">
        <div className="skeleton-header">
          <Skeleton width="200px" height="30px" className="mb-2" />
          <Skeleton width="300px" height="20px" />
        </div>
        <div className="skeleton-grid">
          {[1, 2, 3, 4].map(item => (
            <Card key={item} className="skeleton-summary-card">
              <Skeleton width="100%" height="100px" />
            </Card>
          ))}
        </div>
        <div className="skeleton-table">
          <Skeleton width="100%" height="400px" />
        </div>
      </Card>
    </div>
  );

  return (
    <div className={`stock-assessment-page ${darkTheme ? 'dark-theme' : 'light-theme'}`}>
      <div className="page-container">
        
        {/* Header Section */}
        <div className="page-header">
          <div className="header-content">
            <div className="header-title">
              <i className="pi pi-box header-icon"></i>
              <div>
                <h1>Stock Assessment</h1>
                <p className="header-subtitle">
                  {moment(selectedDate).format('dddd, MMMM Do YYYY')}
                </p>
              </div>
            </div>
            <div className="header-actions">
              <div className="search-box">
                <i className="pi pi-search search-icon"></i>
                <InputText
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search products..."
                  className="search-input"
                  disabled={isFetching || stockArray.length === 0}
                />
                {searchTerm && (
                  <span className="search-count">{filteredStockArray.length} results</span>
                )}
              </div>
              
              <div className="action-buttons">
                <Calendar
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.value)}
                  maxDate={new Date()}
                  dateFormat="dd/mm/yy"
                  showIcon
                  readOnlyInput
                  className="date-picker"
                />
                <Button
                  icon="pi pi-refresh"
                  className="refresh-btn"
                  onClick={() => fetchStockData(selectedDate)}
                  disabled={isFetching}
                  tooltip="Refresh Data"
                />
                <Button
                  label="Export Excel"
                  icon="pi pi-file-excel"
                  className="export-btn"
                  onClick={exportToExcel}
                  disabled={isFetching || filteredStockArray.length === 0}
                />
              </div>
            </div>
          </div>
        </div>

        {loading ? (
          renderSkeleton()
        ) : (
          <>
            {/* Summary Cards */}
            <div className="summary-section">
              <div className="summary-grid">
                {summaryCards.map((card, index) => (
                  <div key={index} className="summary-card" style={{ background: card.gradient }}>
                    <div className="summary-content">
                      <div className="summary-icon" style={{ backgroundColor: card.iconBg }}>
                        <i className={`pi ${card.icon}`}></i>
                      </div>
                      <div className="summary-text">
                        <h3>{card.value}</h3>
                        <p>{card.title}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Data Table Section */}
            <Card className={`data-card ${darkTheme ? 'dark-table' : ''}`}>
              <div className="table-header">
                <h3 className={darkTheme ? 'text-white' : ''}>Product Summary</h3>
                <div className="table-stats">
                  <Tag value={`${filteredStockArray.length} Products`} className="count-tag" />
                  {searchTerm && (
                    <Button 
                      icon="pi pi-times" 
                      className={`p-button-text clear-search ${darkTheme ? 'text-white' : ''}`}
                      onClick={() => setSearchTerm("")}
                      tooltip="Clear search"
                    />
                  )}
                </div>
              </div>

              <DataTable
                value={filteredStockArray}
                loading={isFetching}
                paginator
                rows={10}
                rowsPerPageOptions={[5, 10, 25, 50]}
                sortMode="multiple"
                responsiveLayout="scroll"
                stripedRows={!darkTheme} // disable PrimeReact striped white rows in dark mode
                className={`stock-table ${darkTheme ? 'dark-table' : ''}`}
                emptyMessage={
                  <div className="empty-state">
                    <i className="pi pi-search empty-icon"></i>
                    <h4 className={darkTheme ? 'text-white' : ''}>No products found</h4>
                    <p className={darkTheme ? 'text-gray-300' : ''}>
                      {searchTerm 
                        ? `No products matching "${searchTerm}"` 
                        : "No products ordered on selected date"
                      }
                    </p>
                    {searchTerm && (
                      <Button 
                        label="Clear Search" 
                        icon="pi pi-times" 
                        className="p-button-outlined"
                        onClick={() => setSearchTerm("")}
                      />
                    )}
                  </div>
                }
                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                currentPageReportTemplate="Showing {first} to {last} of {totalRecords} products"
              >
                <Column 
                  header="#" 
                  body={rowNumberBodyTemplate} 
                  style={{ width: '60px' }}
                />
                <Column 
                  field="productName" 
                  header="Product Name" 
                  sortable 
                  style={{ minWidth: '250px' }}
                  body={productNameBodyTemplate}
                />
                <Column 
                  field="totalOrdered" 
                  header="Total" 
                  sortable 
                  style={{ width: '100px' }}
                  body={(rowData) => quantityBodyTemplate(rowData, "totalOrdered")}
                />
                <Column 
                  field="packed" 
                  header="Packed" 
                  sortable 
                  style={{ width: '100px' }}
                  body={(rowData) => quantityBodyTemplate(rowData, "packed")}
                />
                <Column 
                  field="remaining" 
                  header="Remaining" 
                  sortable 
                  style={{ width: '120px' }}
                  body={(rowData) => quantityBodyTemplate(rowData, "remaining")}
                />
                <Column 
                  header="Status" 
                  body={statusBodyTemplate} 
                  style={{ width: '120px' }}
                />
              </DataTable>

              {/* Quick Stats */}
              {filteredStockArray.length > 0 && (
                <div className={`table-footer ${darkTheme ? 'dark-footer' : ''}`}>
                  <div className="footer-stats">
                    <div className="stat-item">
                      <i className="pi pi-check-circle completed-icon"></i>
                      <span className={darkTheme ? 'text-white' : ''}>
                        {filteredStockArray.filter(item => item.remaining === 0).length} Completed
                      </span>
                    </div>
                    <div className="stat-item">
                      <i className="pi pi-clock pending-icon"></i>
                      <span className={darkTheme ? 'text-white' : ''}>
                        {filteredStockArray.filter(item => item.remaining > 0).length} Pending
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </Card>
          </>
        )}
      </div>
    </div>
  );
};

export default StockAssessmentPage;